import React from 'react'
import { useTranslation } from 'react-i18next'
import Spinner from '@components/Spinner'
import { Button } from '@components/Button/Button'
import Footer from '@components/Footer'
import Header from '@components/Header'
import PageSidebar from '@components/Page/PageSidebar'
import Page from '@components/Page'
import TreatmentInfo from '@components/TreatmentInfo'
import { useChoiceState } from '@context/ChoiceContext'
import { useAppointmentCalendar } from '@context/AppointmentCalendarContext/AppointmentCalendarContext'
import useStyles from './AppointmentScreen.styles'
import AppointmentCalendar from './components/AppointmentCalendar'
import { useConfig } from '@context/ConfigurationContext'
import { AppMode } from '@customTypes/Generic'
import { ErrorMessage } from '@components/ErrorMessage/ErrorMessage'
import { PatientTypeOptions } from 'src/config/patientTypeOptions'
import Legal from '../Contact/components/Legal'
import clsx from 'clsx'
import { useCreateBookingForExistingPatient } from '@hooks/useCreateBookingForExistingPatient'
import { completedAppointment } from 'src/services/analytics'
import { useHistory } from 'react-router-dom'
import routes from 'src/config/routes'
import logToPostHog from 'src/helpers/logToPosthog'
import { useGetTreatmentInfo } from '@hooks/useGetTreatmentInfo'
import { useClinicOfficialName } from '@hooks/useClinicOfficialName'
import { isEmpty } from 'lodash'
import { isCurrentPeriodEmpty } from '@context/AppointmentCalendarContext/helpers/isCurrentPeriodEmpty'
import { AppointmentCalendarHeader } from './components/AppointmentCalendar/components/AppointmentCalendarHeader'
import { TEST_IDS } from 'src/constants/testIds'
import { formatError } from './helpers/formatError'
import { useIsPhoneOnly, useIsDesktopUp } from 'src/config/mediaQueries'
import AppointmentInfo from './components/AppointmentInfo'

type AppointmentScreenProps = {
  onBackClicked?: () => void
}

const AppointmentScreen = ({ onBackClicked }: AppointmentScreenProps) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const { mode, clinicId: baseClinicId, isInternalClinic } = useConfig()
  const isPhone = useIsPhoneOnly()
  const isDesktopUp = useIsDesktopUp()
  const isGapFiller = mode === AppMode.GAP_FILLER

  const { selectedBookableDateAndTime, patientType } = useChoiceState()
  const { isFetching, isLoading, currentPeriod, isMoreClinicsOpen } = useAppointmentCalendar()
  const hasSelection = Boolean(selectedBookableDateAndTime.day && selectedBookableDateAndTime.time)
  const isNewPatient = patientType === PatientTypeOptions.NEW
  const {
    createBookingForExistingPatient,
    isLoading: creatingBookingInProgress,
    error,
  } = useCreateBookingForExistingPatient()
  const loading = creatingBookingInProgress || isLoading || (isFetching && isCurrentPeriodEmpty(currentPeriod))
  const isInRecallMode = mode === AppMode.RECALL
  const isInGapFillerMode = mode === AppMode.GAP_FILLER
  const isOldPatientInNormalMode = !isNewPatient && mode === AppMode.NORMAL
  const history = useHistory()
  const treatmentInfo = useGetTreatmentInfo()
  const nearbyClinicName = useClinicOfficialName(selectedBookableDateAndTime.clinicId)
  const baseClinicName = useClinicOfficialName(baseClinicId)

  const handleNextClicked = () => {
    if (selectedBookableDateAndTime.clinicId !== baseClinicId)
      logToPostHog('Nearby timeslot contact screen', { nearbyClinic: nearbyClinicName, baseClinic: baseClinicName })

    completedAppointment()

    if (isInRecallMode || isInGapFillerMode) {
      /**
       * If the app is in RECALL or GAP_FILLER
       * the "/" route represents the confimation screen
       */
      return history.push('/')
    }

    if (isNewPatient || !isInternalClinic) {
      history.push(routes.contact)
    } else {
      createBookingForExistingPatient()
    }
  }

  const footer = (
    <Footer
      beforeButton={
        isOldPatientInNormalMode && isInternalClinic ? (
          <div className={classes.legalTextContainer}>
            <Legal defaultSpacing={false} />
          </div>
        ) : undefined
      }
      button={
        <div>
          <Button
            fullSize
            type="button"
            data-test={TEST_IDS.nextButton}
            text={isOldPatientInNormalMode ? t('book_appointment') : t('next')}
            disabled={!hasSelection || treatmentInfo.isLoading || loading}
            onClick={handleNextClicked}
          />
        </div>
      }
    />
  )

  const PageComponent = isDesktopUp ? PageSidebar : Page

  return (
    <PageComponent
      className={classes.root}
      header={onBackClicked && <Header onBackClick={onBackClicked} hideEmptyHeadingSpacing />}
      sideBar={isDesktopUp && <AppointmentInfo />}
      body={
        <div className={classes.body}>
          <div className={classes.content}>
            <div className={classes.stickyContent}>
              {!isMoreClinicsOpen && (!isEmpty(currentPeriod) || isGapFiller) && !isDesktopUp && (
                <TreatmentInfo
                  data={{ ...treatmentInfo, isLoading: treatmentInfo.isLoading || creatingBookingInProgress }}
                  variant="regular"
                />
              )}
              <div className={clsx(classes.errorContainer, { [classes.hideErrorContainer]: !error })}>
                <ErrorMessage message={formatError(error, t)} />
              </div>
              <AppointmentCalendarHeader />
            </div>
            <div className={classes.calendarCont}>{loading ? <Spinner center /> : <AppointmentCalendar />}</div>
          </div>
        </div>
      }
      footer={isPhone ? hasSelection && footer : footer}
    />
  )
}

export default AppointmentScreen
